<template>
  <div class="app-tabbar" v-show="tabs.length > 0">
    <button
      class="tabbar-scroll-btn"
      v-if="overflowing"
      @click="scroll(-150)"
      v-tooltip.bottom="'Scroll para a esquerda'"
    >
      <i class="pi pi-chevron-left"></i>
    </button>

    <div class="tabbar-list-wrapper" ref="wrapper" @wheel.prevent="onWheel">
      <ul class="tabbar-list" ref="list">
        <li
          v-for="tab in tabs"
          :key="tab.path"
          class="tabbar-item"
          :class="{ 'tabbar-item--active': tab.path === activeTabPath }"
          @click="activateTab(tab)"
          @contextmenu.prevent="openContextMenu($event, tab)"
        >
          <span class="tabbar-item-title">{{ tab.title }}</span>
          <button
            class="tabbar-close-btn"
            @click.stop="closeTab(tab)"
            v-tooltip.bottom="'Fechar'"
          >
            <i class="pi pi-times"></i>
          </button>
        </li>
      </ul>
    </div>

    <button
      class="tabbar-scroll-btn"
      v-if="overflowing"
      @click="scroll(150)"
      v-tooltip.bottom="'Scroll para a direita'"
    >
      <i class="pi pi-chevron-right"></i>
    </button>

    <!-- Context menu -->
    <div
      v-if="contextMenu.visible"
      class="tabbar-context-menu"
      :style="{ top: contextMenu.y + 'px', left: contextMenu.x + 'px' }"
      ref="contextMenu"
      tabindex="-1"
      @blur="closeContextMenu"
    >
      <ul>
        <li @click="ctxCloseTab"><i class="pi pi-times"></i> Fechar</li>
        <li @click="ctxCloseOthers"><i class="pi pi-times-circle"></i> Fechar outras</li>
        <li @click="ctxCloseAll"><i class="pi pi-ban"></i> Fechar todas</li>
      </ul>
    </div>

    <!-- Overlay to detect outside click on context menu -->
    <div
      v-if="contextMenu.visible"
      class="tabbar-context-overlay"
      @click="closeContextMenu"
      @contextmenu.prevent="closeContextMenu"
    ></div>
  </div>
</template>

<script>
export default {
  name: 'AppTabBar',

  data() {
    return {
      overflowing: false,
      contextMenu: {
        visible: false,
        x: 0,
        y: 0,
        tab: null,
      },
    };
  },

  computed: {
    tabs() {
      return this.$store.getters['tabs/tabs'];
    },
    activeTabPath() {
      return this.$store.getters['tabs/activeTabPath'];
    },
  },

  watch: {
    tabs() {
      this.$nextTick(() => this.checkOverflow());
    },
    activeTabPath() {
      this.scrollToActiveTab();
    },
  },

  mounted() {
    this.checkOverflow();
    this._resizeObserver = new ResizeObserver(() => this.checkOverflow());
    if (this.$refs.wrapper) {
      this._resizeObserver.observe(this.$refs.wrapper);
    }
  },

  beforeDestroy() {
    if (this._resizeObserver) {
      this._resizeObserver.disconnect();
    }
  },

  methods: {
    activateTab(tab) {
      this.$store.commit('tabs/SET_ACTIVE_TAB', tab.path);
      this.$router.push(tab.fullPath || tab.path).catch(() => {});
    },

    async closeTab(tab) {
      const nextPath = await this.$store.dispatch('tabs/closeTab', tab.path);
      if (nextPath) {
        this.$router.push(nextPath).catch(() => {});
      } else if (!this.$store.getters['tabs/tabs'].length) {
        this.$router.push('/').catch(() => {});
      }
    },

    scroll(amount) {
      if (this.$refs.wrapper) {
        this.$refs.wrapper.scrollLeft += amount;
      }
    },

    onWheel(event) {
      if (this.$refs.wrapper) {
        this.$refs.wrapper.scrollLeft += event.deltaY || event.deltaX;
      }
    },

    checkOverflow() {
      const wrapper = this.$refs.wrapper;
      if (!wrapper) return;
      this.overflowing = wrapper.scrollWidth > wrapper.clientWidth;
    },

    scrollToActiveTab() {
      this.$nextTick(() => {
        const wrapper = this.$refs.wrapper;
        if (!wrapper) return;
        const activeEl = wrapper.querySelector('.tabbar-item--active');
        if (activeEl) {
          activeEl.scrollIntoView({ inline: 'nearest', block: 'nearest' });
        }
      });
    },

    openContextMenu(event, tab) {
      this.closeContextMenu();
      const rect = this.$el.getBoundingClientRect();
      this.contextMenu = {
        visible: true,
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
        tab,
      };
      this.$nextTick(() => {
        if (this.$refs.contextMenu) this.$refs.contextMenu.focus();
      });
    },

    closeContextMenu() {
      this.contextMenu.visible = false;
      this.contextMenu.tab = null;
    },

    ctxCloseTab() {
      const tab = this.contextMenu.tab;
      this.closeContextMenu();
      if (tab) this.closeTab(tab);
    },

    async ctxCloseOthers() {
      const tab = this.contextMenu.tab;
      this.closeContextMenu();
      if (!tab) return;
      await this.$store.dispatch('tabs/closeOtherTabs', tab.path);
      this.$router.push(tab.path).catch(() => {});
    },

    async ctxCloseAll() {
      this.closeContextMenu();
      await this.$store.dispatch('tabs/closeAllTabs');
      this.$router.push('/').catch(() => {});
    },
  },
};
</script>

<style scoped>
.app-tabbar {
  display: flex;
  align-items: center;
  background: #404353;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  height: 40px;
  min-height: 40px;
  position: sticky;
  top: 0;
  z-index: 100;
  padding: 0 8px;
  gap: 4px;
}

.tabbar-list-wrapper {
  flex: 1;
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: none;
  position: relative;
}

.tabbar-list-wrapper::-webkit-scrollbar {
  display: none;
}

.tabbar-list {
  display: flex;
  list-style: none;
  margin: 0;
  padding: 0;
  gap: 4px;
  height: 40px;
  align-items: center;
}

.tabbar-item {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 0 12px;
  height: 30px;
  border-radius: 6px;
  background: rgba(30, 32, 44, 0.8);
  color: #a3b4ca;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  font-size: 13px;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  user-select: none;
  border: 1px solid rgba(255, 255, 255, 0.02);
}

/* Smooth hover micro-animation */
.tabbar-item:hover {
  background: rgba(255, 255, 255, 0.1);
  color: #ffffff;
  transform: translateY(-1px);
}

/* Active state with gentle bottom-border line or glowing white background */
.tabbar-item--active {
  background: #ffffff;
  color: #1a202c;
  font-weight: 500;
  border-color: #ffffff;
}

.tabbar-item--active:hover {
  background: #ffffff;
  color: #1a202c;
  transform: none; /* Keep active stable */
}

.tabbar-item-title {
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.tabbar-close-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border: none;
  background: transparent;
  cursor: pointer;
  border-radius: 50%;
  color: inherit;
  padding: 0;
  opacity: 0.4;
  transition: all 0.15s;
  flex-shrink: 0;
}

.tabbar-item:hover .tabbar-close-btn,
.tabbar-item--active .tabbar-close-btn {
  opacity: 0.7;
}

.tabbar-close-btn:hover {
  opacity: 1 !important;
  background: rgba(0, 0, 0, 0.1);
  color: #e53e3e !important; /* Elegant red accent when hovering X */
}

.tabbar-item--active .tabbar-close-btn:hover {
  background: rgba(0, 0, 0, 0.08);
}

.tabbar-close-btn .pi {
  font-size: 8px;
}

.tabbar-scroll-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 30px;
  border: none;
  background: rgba(255, 255, 255, 0.1);
  color: #a0aec0;
  cursor: pointer;
  border-radius: 6px;
  flex-shrink: 0;
  transition: all 0.2s;
  padding: 0;
}

.tabbar-scroll-btn:hover {
  background: rgba(255, 255, 255, 0.15);
  color: #ffffff;
}

.tabbar-scroll-btn .pi {
  font-size: 10px;
}

/* Glassmorphic Context Menu */
.tabbar-context-overlay {
  position: fixed;
  inset: 0;
  z-index: 199;
}

.tabbar-context-menu {
  position: absolute;
  z-index: 200;
  background: rgba(255, 255, 255, 0.85); /* Semi-transparent white */
  backdrop-filter: blur(12px); /* Premium OS-style blur */
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 8px;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1);
  min-width: 170px;
  overflow: hidden;
  outline: none;
}

.tabbar-context-menu ul {
  list-style: none;
  margin: 0;
  padding: 5px 0;
}

.tabbar-context-menu li {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 16px;
  font-size: 13px;
  color: #1a202c;
  cursor: pointer;
  transition: all 0.15s ease;
}

.tabbar-context-menu li:hover {
  background: rgba(0, 0, 0, 0.05);
}

.tabbar-context-menu li .pi {
  font-size: 12px;
  color: #4a5568;
}
</style>
